import { useContext, useEffect, useState } from "react";
import { Table, InputNumber, Button, message } from "antd";
import { useSlaTargets, useUpdateSlaTargets } from "../../queries/settings";
import type { SlaTargets } from "../../queries/reports";
import { ApiError } from "../../hooks/apiRequest";
import { PageShell } from "../../components/ui/PageShell";
import { NoAccess } from "../../components/ui/NoAccess";
import { SeverityChip } from "../../components/ui/StatusTag";
import { DisplayComponent } from "../../navigation/layout/DisplayComponent";
import { SEVERITIES } from "../../utils/levels";
import { formatMinutes } from "../../utils/duration";
import { Severity } from "../../types/interfaces";
import AuthContext from "../../context/AuthContext";

type Field = "firstResponseMinutes" | "resolutionMinutes";

/** Response-time targets (SLA) per ticket severity — drives the manager dashboard. */
export const SettingsPage = () => {
  const { isManager } = useContext(AuthContext);
  const { data, isLoading, isError, error } = useSlaTargets();
  const update = useUpdateSlaTargets();
  const [draft, setDraft] = useState<SlaTargets | null>(null);

  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  if (!isManager) return <NoAccess what="settings" />;

  const setValue = (severity: Severity, field: Field, value: number | null) => {
    if (!draft || !value || value < 1) return;
    setDraft({ ...draft, [severity]: { ...draft[severity], [field]: value } });
  };

  const save = async () => {
    if (!draft) return;
    try {
      await update.mutateAsync(draft);
      message.success("Response-time targets saved");
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not save the targets");
    }
  };

  const dirty = !!draft && !!data && JSON.stringify(draft) !== JSON.stringify(data);

  return (
    <PageShell
      kind="detail"
      kicker="Administration"
      title="Settings"
      subtitle="Response-time targets by ticket severity. Tickets that exceed them are flagged on the manager dashboard."
      actions={<Button type="primary" onClick={save} disabled={!dirty} loading={update.isPending}>Save targets</Button>}
    >
      <DisplayComponent error={isError ? error : undefined} isLoading={isLoading || !draft}>
        {draft && (
          <Table
            rowKey="severity"
            size="small"
            pagination={false}
            dataSource={SEVERITIES.map((severity) => ({ severity, ...draft[severity] }))}
            columns={[
              { title: "Severity", dataIndex: "severity", width: 150, render: (v: Severity) => <SeverityChip severity={v} /> },
              ...(["firstResponseMinutes", "resolutionMinutes"] as Field[]).map((field) => ({
                title: field === "firstResponseMinutes" ? "First response within" : "Resolution within",
                dataIndex: field,
                render: (v: number, row: { severity: Severity }) => (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 12 }}>
                    <InputNumber
                      min={1}
                      value={v}
                      aria-label={`${row.severity} ${field === "firstResponseMinutes" ? "first response" : "resolution"} minutes`}
                      addonAfter="min"
                      onChange={(n) => setValue(row.severity, field, n)}
                      style={{ width: 150 }}
                    />
                    <span className="osi-num" style={{ color: "var(--osi-text-muted)" }}>= {formatMinutes(v)}</span>
                  </span>
                ),
              })),
            ]}
          />
        )}
      </DisplayComponent>
    </PageShell>
  );
};
